import { currencyPrice } from '@payaca/helpers/financeHelper';
import { BrandingColourContext } from '@payaca/types/accountBrandTypes';
import { AccountRegions } from '@payaca/types/accountTypes';
import { ClientInvoiceGrant } from '@payaca/types/clientTypes';
import { DealTotals } from '@payaca/types/dealTypes';
import { Invoice, InvoiceLine } from '@payaca/types/invoiceTypes';
import { JobLineItem } from '@payaca/types/jobContentTypes';
import moment from 'moment-timezone';
import React, { FunctionComponent, useMemo, type JSX } from 'react';

type SummaryInvoice = Pick<
  Invoice,
  'id' | 'sentAt' | 'voidedAt' | 'totalValue'
> & {
  isGrantInvoice?: boolean;
  grantLine?: ClientInvoiceGrant | null;
};

type Props = {
  invoiceId: number;
  invoices: SummaryInvoice[];
  dealTotals: Pick<DealTotals, 'acceptedValue'>;
  jobLineItems: JobLineItem[];
  invoiceLines: InvoiceLine[];
  region?: AccountRegions;
  brandingColourContext: BrandingColourContext;
  isStraightToInvoice?: boolean;
  /** The Project's invoice grants, listed as deductions. */
  invoiceGrants?: ClientInvoiceGrant[];
};

const InvoiceSummary: FunctionComponent<React.PropsWithChildren<Props>> = ({
  invoices,
  invoiceId,
  dealTotals,
  region,
  isStraightToInvoice = false,
  invoiceGrants = [],
}: React.PropsWithChildren<Props>): JSX.Element | null => {
  const invoice = useMemo(() => {
    return invoices.find((x) => x.id == invoiceId);
  }, [invoices, invoiceId]);

  const previouslySentInvoices = useMemo(() => {
    if (!invoice) return [];
    const invoiceSentAt = invoice.sentAt ? moment(invoice.sentAt) : moment();
    return invoices.filter((x) => {
      // Grant invoices are never collectable from the Customer, so they are kept
      // out of the "previously invoiced" figure.
      return (
        !x.isGrantInvoice &&
        moment(x.sentAt).isBefore(invoiceSentAt) &&
        (!x.voidedAt || moment(x.voidedAt).isAfter(invoiceSentAt))
      );
    });
  }, [invoice, invoices]);

  const previouslyInvoicedValue = useMemo(() => {
    return previouslySentInvoices.reduce(
      (acc, invoice) => acc + invoice.totalValue,
      0
    );
  }, [previouslySentInvoices]);

  const grantTotal = useMemo(() => {
    return invoiceGrants.reduce(
      (acc, grant) => acc + grant.amountMinorUnits,
      0
    );
  }, [invoiceGrants]);

  const isGrantInvoice = !!invoice?.isGrantInvoice;

  // The grants listed above "Yet to invoice". On a grant invoice the grant it
  // represents is shown in bold at the end instead, so it is excluded here by
  // its grant line id.
  const listedGrants = useMemo(() => {
    if (!isGrantInvoice || !invoice?.grantLine) return invoiceGrants;
    const representedGrantLineId = invoice.grantLine.grantLineId;
    return invoiceGrants.filter(
      (grant) => grant.grantLineId !== representedGrantLineId
    );
  }, [invoiceGrants, isGrantInvoice, invoice]);

  const stillToInvoiceValue = useMemo(() => {
    // A grant invoice's own total is part of the grant total already, so it is
    // not subtracted again.
    return (
      dealTotals.acceptedValue -
      previouslyInvoicedValue -
      grantTotal -
      (isGrantInvoice ? 0 : invoice?.totalValue || 0)
    );
  }, [
    grantTotal,
    isGrantInvoice,
    invoice?.totalValue,
    dealTotals.acceptedValue,
    previouslyInvoicedValue,
  ]);

  if (!invoice) return null;

  return (
    <>
      <h2 className="mb-5 text-lg">Summary</h2>

      <table className="w-full border-collapse text-base">
        <tbody>
          {!isStraightToInvoice && (
            <tr>
              <td className="pb-1 pr-2">Agreed project total</td>
              <td className="pb-1 text-right align-bottom">
                {currencyPrice(dealTotals.acceptedValue, region)}
              </td>
            </tr>
          )}

          {previouslyInvoicedValue > 0 && (
            <tr>
              <td className="py-1 pr-2">Previously invoiced</td>
              <td className="py-1 text-right align-bottom">
                {currencyPrice(previouslyInvoicedValue, region)}
              </td>
            </tr>
          )}

          {listedGrants.map((grant, i) => (
            <tr key={i}>
              <td className="py-1 pr-2">{grant.name}</td>
              <td className="py-1 text-right align-bottom">
                {currencyPrice(-grant.amountMinorUnits, region)}
              </td>
            </tr>
          ))}

          {stillToInvoiceValue > 0 && (
            <tr>
              <td className="py-1 pr-2">Yet to invoice</td>
              <td className="py-1 text-right align-bottom">
                {currencyPrice(stillToInvoiceValue, region)}
              </td>
            </tr>
          )}

          <tr>
            <td className="pr-2 pt-1 font-bold">
              {invoice.grantLine?.name ?? 'Due on this invoice'}
            </td>
            <td className="pt-1 text-right align-bottom font-bold">
              {currencyPrice(
                invoice.grantLine?.amountMinorUnits ?? invoice.totalValue,
                region
              )}
            </td>
          </tr>
        </tbody>
      </table>

      {invoice.grantLine && (
        <p className="mt-3 text-base">
          This invoice is for a grant. You do not need to pay it.
        </p>
      )}
    </>
  );
};

export default InvoiceSummary;
